/**
 * Admin context for multi-tenant admin dashboard and user management
 */

import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { adminService } from '../services/adminService';
import {
  AdminDashboardResponse,
  UserManagementResponse,
  SystemMetricsResponse,
  SupportTicket,
  SupportTicketMessage,
  SupportTicketUpdateRequest,
  AuditLog,
  SecurityEvent,
  AdminUser,
  AdminEmailConfig,
  SystemConfig,
  UserFilters,
  AuditLogFilters,
  SupportTicketFilters,
  SecurityEventFilters
} from '../types/admin';

/** Normalize backend dashboard response to AdminDashboardResponse shape (handles both backend and legacy shapes). */
function normalizeDashboardResponse(data: any): AdminDashboardResponse {
  if (!data || typeof data !== 'object') {
    return getDefaultDashboardResponse();
  }
  const sh = data.system_health ?? data.overview?.system_health;
  const us = data.user_statistics ?? data.overview?.user_statistics;
  const ss = data.subscription_statistics ?? data.overview?.subscription_statistics;
  const activeAlerts = data.active_alerts ?? data.alerts ?? data.overview?.active_alerts ?? [];
  const overviewBlock = data.system_overview ?? data.overview?.system_overview;

  const system_health = {
    status: (sh?.status ?? sh?.overall_status ?? 'healthy') as 'healthy' | 'warning' | 'error',
    cpu_usage: typeof sh?.cpu_usage === 'number' ? sh.cpu_usage : 0,
    memory_usage: typeof sh?.memory_usage === 'number' ? sh.memory_usage : 0,
    disk_usage: typeof sh?.disk_usage === 'number' ? sh.disk_usage : 0,
    database_status: sh?.database_status ?? 'healthy',
    email_service_status: sh?.email_service_status ?? 'healthy',
    webhook_service_status: sh?.webhook_service_status ?? 'healthy',
  };

  const system_overview = overviewBlock ?? {
    total_users: us?.total_users ?? 0,
    active_users: us?.active_users_today ?? 0,
    total_subscriptions: ss?.total_subscriptions ?? 0,
    active_subscriptions: ss?.active_subscriptions ?? 0,
    total_webhooks: 0,
    active_webhooks: 0,
    total_alerts_processed: 0,
    alerts_processed_today: 0,
  };

  const user_metrics = data.user_metrics ?? {
    new_users_today: 0,
    new_users_this_week: us?.new_users_this_week ?? 0,
    new_users_this_month: 0,
    active_users_today: us?.active_users_today ?? 0,
    active_users_this_week: 0,
    subscription_distribution: {},
  };

  const alerts = Array.isArray(activeAlerts)
    ? activeAlerts.map((a: any, i: number) => ({
        id: a.id ?? i,
        alert_type: a.alert_type ?? 'system',
        severity: a.severity ?? a.alert_level ?? 'info',
        message: a.message ?? a.alert_message ?? 'Alert',
        timestamp: a.timestamp ?? a.created_at ?? new Date().toISOString(),
        resolved: a.resolved ?? a.is_resolved ?? false,
      }))
    : [];

  const recent_activity = Array.isArray(data.recent_activity) ? data.recent_activity : [];

  return {
    system_overview,
    user_metrics,
    system_health,
    recent_activity,
    alerts,
    tenant_id: data.tenant_id ?? '',
  };
}

function normalizeSecurityEvent(raw: any, index: number): SecurityEvent {
  return {
    id: raw.id ?? index,
    tenant_id: raw.tenant_id ?? '',
    user_id: raw.user_id,
    admin_id: raw.admin_id,
    event_type: raw.event_type ?? 'unknown',
    severity: raw.severity ?? 'low',
    description: raw.description ?? raw.event_description ?? '',
    ip_address: raw.ip_address,
    user_agent: raw.user_agent,
    details: raw.details ?? raw.event_data,
    resolved: raw.resolved ?? raw.is_resolved ?? false,
    resolved_at: raw.resolved_at,
    resolved_by: raw.resolved_by,
    created_at: raw.created_at ?? new Date().toISOString(),
  };
}

function normalizeAuditLog(raw: any, index: number): AuditLog {
  const ts = raw.created_at ?? raw.timestamp;
  return {
    id: raw.id ?? index,
    tenant_id: raw.tenant_id ?? '',
    user_id: raw.user_id ? Number(raw.user_id) : undefined,
    admin_id: raw.admin_id ? Number(raw.admin_id) : undefined,
    action: raw.action ?? raw.action_type ?? 'unknown',
    resource_type: raw.resource_type ?? '',
    resource_id: raw.resource_id ?? '',
    details: raw.details ?? {
      description: raw.action_description,
      old_values: raw.old_values,
      new_values: raw.new_values,
    },
    ip_address: raw.ip_address,
    user_agent: raw.user_agent,
    created_at: typeof ts === 'string' ? ts : ts?.toString?.() ?? new Date().toISOString(),
  };
}

function getDefaultDashboardResponse(): AdminDashboardResponse {
  return {
    system_overview: {
      total_users: 0,
      active_users: 0,
      total_subscriptions: 0,
      active_subscriptions: 0,
      total_webhooks: 0,
      active_webhooks: 0,
      total_alerts_processed: 0,
      alerts_processed_today: 0,
    },
    user_metrics: {
      new_users_today: 0,
      new_users_this_week: 0,
      new_users_this_month: 0,
      active_users_today: 0,
      active_users_this_week: 0,
      subscription_distribution: {},
    },
    system_health: {
      status: 'healthy',
      cpu_usage: 0,
      memory_usage: 0,
      disk_usage: 0,
      database_status: 'healthy',
      email_service_status: 'healthy',
      webhook_service_status: 'healthy',
    },
    recent_activity: [],
    alerts: [],
    tenant_id: '',
  };
}

interface AdminContextType {
  // Dashboard data
  dashboardData: AdminDashboardResponse | null;
  systemMetrics: SystemMetricsResponse | null;
  loading: boolean;
  error: string | null;
  
  // User management
  users: UserManagementResponse | null;
  usersLoading: boolean;
  usersError: string | null;
  
  // Support tickets
  supportTickets: SupportTicket[];
  supportTicketsLoading: boolean;
  supportTicketsError: string | null;
  
  // Audit logs
  auditLogs: AuditLog[];
  auditLogTotal: number;
  auditLogsLoading: boolean;
  auditLogsError: string | null;
  
  // Security events
  securityEvents: SecurityEvent[];
  securityEventsLoading: boolean;
  securityEventsError: string | null;
  
  // Admin users
  adminUsers: AdminUser[];
  adminUsersLoading: boolean;
  adminUsersError: string | null;
  
  // Email configs
  emailConfigs: AdminEmailConfig[];
  emailConfigsLoading: boolean;
  emailConfigsError: string | null;
  
  // System configs
  systemConfigs: SystemConfig[];
  systemConfigsLoading: boolean;
  systemConfigsError: string | null;
  
  // Actions
  loadDashboard: () => Promise<void>;
  loadSystemMetrics: (metricName?: string, hours?: number) => Promise<void>;
  loadUsers: (filters?: UserFilters) => Promise<void>;
  loadSupportTickets: (filters?: SupportTicketFilters) => Promise<void>;
  loadAuditLogs: (filters?: AuditLogFilters) => Promise<void>;
  loadSecurityEvents: (filters?: SecurityEventFilters) => Promise<void>;
  loadAdminUsers: () => Promise<void>;
  loadEmailConfigs: () => Promise<void>;
  loadSystemConfigs: () => Promise<void>;
  
  // User management actions
  updateUser: (userId: number, updateData: Record<string, any>) => Promise<void>;
  suspendUser: (userId: number) => Promise<void>;
  activateUser: (userId: number) => Promise<void>;
  
  // Support ticket actions
  updateSupportTicket: (ticketId: string, updateData: SupportTicketUpdateRequest) => Promise<void>;
  assignSupportTicket: (ticketId: string, assignedTo: string | null) => Promise<void>;
  respondToTicket: (ticketId: string, messageContent: string, isInternal?: boolean) => Promise<void>;
  getTicketMessages: (ticketId: string) => Promise<SupportTicketMessage[]>;
  
  // Admin user actions
  createAdminUser: (adminData: any) => Promise<void>;
  updateAdminUser: (adminId: number, updateData: any) => Promise<void>;
  deleteAdminUser: (adminId: number) => Promise<void>;
  
  // Email config actions
  createEmailConfig: (configData: any) => Promise<void>;
  updateEmailConfig: (configId: number, updateData: any) => Promise<void>;
  deleteEmailConfig: (configId: number) => Promise<void>;
  
  // System config actions
  createSystemConfig: (configData: any) => Promise<void>;
  updateSystemConfig: (configId: number, updateData: any) => Promise<void>;
  deleteSystemConfig: (configId: number) => Promise<void>;
  
  // Security actions
  createSecurityIncident: (incidentData: any) => Promise<void>;
  resolveSecurityEvent: (eventId: string | number, notes?: string) => Promise<void>;
  
  // Utility functions
  refreshData: () => Promise<void>;
  clearError: () => void;
}

const AdminContext = createContext<AdminContextType | undefined>(undefined);

interface AdminProviderProps {
  children: ReactNode;
}

export const AdminProvider: React.FC<AdminProviderProps> = ({ children }) => {
  // Dashboard data state
  const [dashboardData, setDashboardData] = useState<AdminDashboardResponse | null>(null);
  const [systemMetrics, setSystemMetrics] = useState<SystemMetricsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  
  // User management state
  const [users, setUsers] = useState<UserManagementResponse | null>(null);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState<string | null>(null);
  
  // Support tickets state
  const [supportTickets, setSupportTickets] = useState<SupportTicket[]>([]);
  const [supportTicketsLoading, setSupportTicketsLoading] = useState(false);
  const [supportTicketsError, setSupportTicketsError] = useState<string | null>(null);
  
  // Audit logs state
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [auditLogTotal, setAuditLogTotal] = useState(0);
  const [auditLogsLoading, setAuditLogsLoading] = useState(false);
  const [auditLogsError, setAuditLogsError] = useState<string | null>(null);
  
  // Security events state
  const [securityEvents, setSecurityEvents] = useState<SecurityEvent[]>([]);
  const [securityEventsLoading, setSecurityEventsLoading] = useState(false);
  const [securityEventsError, setSecurityEventsError] = useState<string | null>(null);
  
  // Admin users state
  const [adminUsers, setAdminUsers] = useState<AdminUser[]>([]);
  const [adminUsersLoading, setAdminUsersLoading] = useState(false);
  const [adminUsersError, setAdminUsersError] = useState<string | null>(null);
  
  // Email configs state
  const [emailConfigs, setEmailConfigs] = useState<AdminEmailConfig[]>([]);
  const [emailConfigsLoading, setEmailConfigsLoading] = useState(false);
  const [emailConfigsError, setEmailConfigsError] = useState<string | null>(null);
  
  // System configs state
  const [systemConfigs, setSystemConfigs] = useState<SystemConfig[]>([]);
  const [systemConfigsLoading, setSystemConfigsLoading] = useState(false);
  const [systemConfigsError, setSystemConfigsError] = useState<string | null>(null);
  
  // Dashboard actions
  const loadDashboard = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await adminService.getDashboard();
      setDashboardData(normalizeDashboardResponse(data));
    } catch (err: any) {
      const errorMessage = err?.message || err?.error?.message || 'Failed to load dashboard data';
      setError(errorMessage);
      console.error('Error loading admin dashboard:', err);
    } finally {
      setLoading(false);
    }
  }, []);
  
  const loadSystemMetrics = useCallback(async (metricName?: string, hours: number = 24) => {
    try {
      const data = await adminService.getSystemMetrics(metricName, hours);
      const metrics = Array.isArray(data?.metrics) ? data.metrics : [];
      setSystemMetrics({
        ...data,
        metrics: metrics.map((metric: any, index: number) => ({
          ...metric,
          id: metric.id ?? index,
          timestamp:
            typeof metric.timestamp === 'string'
              ? metric.timestamp
              : metric.timestamp?.toString?.() ?? new Date().toISOString(),
        })),
      });
    } catch (err: any) {
      const errorMessage = err?.message || err?.error?.message || 'Failed to load system metrics';
      setError(errorMessage);
      console.error('Error loading system metrics:', err);
    }
  }, []);
  
  // User management actions
  const loadUsers = useCallback(async (filters?: UserFilters) => {
    setUsersLoading(true);
    setUsersError(null);
    try {
      const data = await adminService.getUsers(filters);
      setUsers(data);
    } catch (err: any) {
      const errorMessage = err?.message || err?.error?.message || 'Failed to load users';
      setUsersError(errorMessage);
      console.error('Error loading users:', err);
    } finally {
      setUsersLoading(false);
    }
  }, []);
  
  const updateUser = async (userId: number, updateData: Record<string, any>) => {
    try {
      await adminService.updateUser(userId, updateData);
      // Refresh users list
      await loadUsers();
    } catch (err: any) {
      setUsersError(err.message || 'Failed to update user');
      throw err;
    }
  };
  
  const suspendUser = async (userId: number) => {
    try {
      await adminService.suspendUser(userId);
      // Refresh users list
      await loadUsers();
    } catch (err: any) {
      setUsersError(err.message || 'Failed to suspend user');
      throw err;
    }
  };
  
  const activateUser = async (userId: number) => {
    try {
      await adminService.activateUser(userId);
      // Refresh users list
      await loadUsers();
    } catch (err: any) {
      setUsersError(err.message || 'Failed to activate user');
      throw err;
    }
  };
  
  // Support ticket actions
  const loadSupportTickets = useCallback(async (filters?: SupportTicketFilters) => {
    setSupportTicketsLoading(true);
    setSupportTicketsError(null);
    try {
      const data = await adminService.getSupportTickets(filters);
      setSupportTickets(data);
    } catch (err: any) {
      const errorMessage = err?.message || err?.error?.message || 'Failed to load support tickets';
      setSupportTicketsError(errorMessage);
      console.error('Error loading support tickets:', err);
    } finally {
      setSupportTicketsLoading(false);
    }
  }, []);
  
  const updateSupportTicket = async (ticketId: string, updateData: SupportTicketUpdateRequest) => {
    try {
      await adminService.updateSupportTicket(ticketId, updateData);
      // Refresh support tickets list
      await loadSupportTickets();
    } catch (err: any) {
      setSupportTicketsError(err.message || 'Failed to update support ticket');
      throw err;
    }
  };
  
  const assignSupportTicket = async (ticketId: string, assignedTo: string | null) => {
    try {
      await adminService.assignSupportTicket(ticketId, assignedTo);
      // Refresh support tickets list
      await loadSupportTickets();
    } catch (err: any) {
      setSupportTicketsError(err.message || 'Failed to assign support ticket');
      throw err;
    }
  };

  const respondToTicket = async (ticketId: string, messageContent: string, isInternal = false) => {
    try {
      await adminService.addTicketMessage(ticketId, messageContent, isInternal);
      // Refresh so updated_at and any status-derived ordering reflect the reply
      await loadSupportTickets();
    } catch (err: any) {
      setSupportTicketsError(err.message || 'Failed to send reply');
      throw err;
    }
  };

  const getTicketMessages = async (ticketId: string): Promise<SupportTicketMessage[]> => {
    return adminService.getTicketMessages(ticketId);
  };
  
  // Audit log actions
  const loadAuditLogs = useCallback(async (filters?: AuditLogFilters) => {
    setAuditLogsLoading(true);
    setAuditLogsError(null);
    try {
      const data = await adminService.getAuditLogs(filters);
      setAuditLogs((data.logs ?? []).map(normalizeAuditLog));
      setAuditLogTotal(data.total_count ?? 0);
    } catch (err: any) {
      const errorMessage = err?.message || err?.error?.message || 'Failed to load audit logs';
      setAuditLogsError(errorMessage);
      console.error('Error loading audit logs:', err);
    } finally {
      setAuditLogsLoading(false);
    }
  }, []);
  
  // Security event actions
  const loadSecurityEvents = useCallback(async (filters?: SecurityEventFilters) => {
    setSecurityEventsLoading(true);
    setSecurityEventsError(null);
    try {
      const data = await adminService.getSecurityEvents(filters);
      const rows = Array.isArray(data) ? data : [];
      setSecurityEvents(rows.map(normalizeSecurityEvent));
    } catch (err: any) {
      const errorMessage = err?.message || err?.error?.message || 'Failed to load security events';
      setSecurityEventsError(errorMessage);
      console.error('Error loading security events:', err);
    } finally {
      setSecurityEventsLoading(false);
    }
  }, []);
  
  const createSecurityIncident = async (incidentData: any) => {
    try {
      await adminService.createSecurityIncident(incidentData);
      await loadSecurityEvents();
    } catch (err: any) {
      setSecurityEventsError(err.message || 'Failed to create security incident');
      throw err;
    }
  };

  const resolveSecurityEvent = async (eventId: string | number, notes?: string) => {
    try {
      await adminService.updateSecurityEvent(eventId, {
        resolved: true,
        resolution_notes: notes,
      });
      await loadSecurityEvents();
    } catch (err: any) {
      setSecurityEventsError(err.message || 'Failed to resolve security event');
      throw err;
    }
  };
  
  // Admin user actions
  const loadAdminUsers = useCallback(async () => {
    setAdminUsersLoading(true);
    setAdminUsersError(null);
    try {
      const data = await adminService.getAdminUsers();
      setAdminUsers(data);
    } catch (err: any) {
      const errorMessage = err?.message || err?.error?.message || 'Failed to load admin users';
      setAdminUsersError(errorMessage);
      console.error('Error loading admin users:', err);
    } finally {
      setAdminUsersLoading(false);
    }
  }, []);
  
  const createAdminUser = async (adminData: any) => {
    try {
      await adminService.createAdminUser(adminData);
      // Refresh admin users list
      await loadAdminUsers();
    } catch (err: any) {
      setAdminUsersError(err.message || 'Failed to create admin user');
      throw err;
    }
  };
  
  const updateAdminUser = async (adminId: number, updateData: any) => {
    try {
      await adminService.updateAdminUser(adminId, updateData);
      // Refresh admin users list
      await loadAdminUsers();
    } catch (err: any) {
      setAdminUsersError(err.message || 'Failed to update admin user');
      throw err;
    }
  };
  
  const deleteAdminUser = async (adminId: number) => {
    try {
      await adminService.deleteAdminUser(adminId);
      // Refresh admin users list
      await loadAdminUsers();
    } catch (err: any) {
      setAdminUsersError(err.message || 'Failed to delete admin user');
      throw err;
    }
  };
  
  // Email config actions
  const loadEmailConfigs = useCallback(async () => {
    setEmailConfigsLoading(true);
    setEmailConfigsError(null);
    try {
      const data = await adminService.getAdminEmailConfigs();
      setEmailConfigs(data);
    } catch (err: any) {
      const errorMessage = err?.message || err?.error?.message || 'Failed to load email configs';
      setEmailConfigsError(errorMessage);
      console.error('Error loading email configs:', err);
    } finally {
      setEmailConfigsLoading(false);
    }
  }, []);
  
  const createEmailConfig = async (configData: any) => {
    try {
      await adminService.createAdminEmailConfig(configData);
      // Refresh email configs list
      await loadEmailConfigs();
    } catch (err: any) {
      setEmailConfigsError(err.message || 'Failed to create email config');
      throw err;
    }
  };
  
  const updateEmailConfig = async (configId: number, updateData: any) => {
    try {
      await adminService.updateAdminEmailConfig(configId, updateData);
      // Refresh email configs list
      await loadEmailConfigs();
    } catch (err: any) {
      setEmailConfigsError(err.message || 'Failed to update email config');
      throw err;
    }
  };
  
  const deleteEmailConfig = async (configId: number) => {
    try {
      await adminService.deleteAdminEmailConfig(configId);
      // Refresh email configs list
      await loadEmailConfigs();
    } catch (err: any) {
      setEmailConfigsError(err.message || 'Failed to delete email config');
      throw err;
    }
  };
  
  // System config actions
  const loadSystemConfigs = useCallback(async () => {
    setSystemConfigsLoading(true);
    setSystemConfigsError(null);
    try {
      const data = await adminService.getSystemConfigs();
      setSystemConfigs(data);
    } catch (err: any) {
      const errorMessage = err?.message || err?.error?.message || 'Failed to load system configs';
      setSystemConfigsError(errorMessage);
      console.error('Error loading system configs:', err);
    } finally {
      setSystemConfigsLoading(false);
    }
  }, []);
  
  const createSystemConfig = async (configData: any) => {
    try {
      await adminService.createSystemConfig(configData);
      // Refresh system configs list
      await loadSystemConfigs();
    } catch (err: any) {
      setSystemConfigsError(err.message || 'Failed to create system config');
      throw err;
    }
  };
  
  const updateSystemConfig = async (configId: number, updateData: any) => {
    try {
      await adminService.updateSystemConfig(configId, updateData);
      // Refresh system configs list
      await loadSystemConfigs();
    } catch (err: any) {
      setSystemConfigsError(err.message || 'Failed to update system config');
      throw err;
    }
  };
  
  const deleteSystemConfig = async (configId: number) => {
    try {
      await adminService.deleteSystemConfig(configId);
      // Refresh system configs list
      await loadSystemConfigs();
    } catch (err: any) {
      setSystemConfigsError(err.message || 'Failed to delete system config');
      throw err;
    }
  };
  
  // Utility functions
  const refreshData = useCallback(async () => {
    await Promise.all([
      loadDashboard(),
      loadUsers(),
      loadSupportTickets(),
      loadAuditLogs(),
      loadSecurityEvents(),
      loadAdminUsers(),
      loadEmailConfigs(),
      loadSystemConfigs()
    ]);
  }, [loadDashboard, loadUsers, loadSupportTickets, loadAuditLogs, loadSecurityEvents, loadAdminUsers, loadEmailConfigs, loadSystemConfigs]);
  
  const clearError = () => {
    setError(null);
    setUsersError(null);
    setSupportTicketsError(null);
    setAuditLogsError(null);
    setSecurityEventsError(null);
    setAdminUsersError(null);
    setEmailConfigsError(null);
    setSystemConfigsError(null);
  };
  
  // Load initial data
  useEffect(() => {
    loadDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load on mount only
  }, []);
  
  const value: AdminContextType = {
    // Dashboard data
    dashboardData,
    systemMetrics,
    loading,
    error,
    
    // User management
    users,
    usersLoading,
    usersError,
    
    // Support tickets
    supportTickets,
    supportTicketsLoading,
    supportTicketsError,
    
    // Audit logs
    auditLogs,
    auditLogTotal,
    auditLogsLoading,
    auditLogsError,
    
    // Security events
    securityEvents,
    securityEventsLoading,
    securityEventsError,
    
    // Admin users
    adminUsers,
    adminUsersLoading,
    adminUsersError,
    
    // Email configs
    emailConfigs,
    emailConfigsLoading,
    emailConfigsError,
    
    // System configs
    systemConfigs,
    systemConfigsLoading,
    systemConfigsError,
    
    // Actions
    loadDashboard,
    loadSystemMetrics,
    loadUsers,
    loadSupportTickets,
    loadAuditLogs,
    loadSecurityEvents,
    loadAdminUsers,
    loadEmailConfigs,
    loadSystemConfigs,
    
    // User management actions
    updateUser,
    suspendUser,
    activateUser,
    
    // Support ticket actions
    updateSupportTicket,
    assignSupportTicket,
    respondToTicket,
    getTicketMessages,
    
    // Admin user actions
    createAdminUser,
    updateAdminUser,
    deleteAdminUser,
    
    // Email config actions
    createEmailConfig,
    updateEmailConfig,
    deleteEmailConfig,
    
    // System config actions
    createSystemConfig,
    updateSystemConfig,
    deleteSystemConfig,
    
    // Security actions
    createSecurityIncident,
    resolveSecurityEvent,
    
    // Utility functions
    refreshData,
    clearError
  };
  
  return (
    <AdminContext.Provider value={value}>
      {children}
    </AdminContext.Provider>
  );
};

export const useAdmin = (): AdminContextType => {
  const context = useContext(AdminContext);
  if (context === undefined) {
    throw new Error('useAdmin must be used within an AdminProvider');
  }
  return context;
};
